import type { ChatThreadSummary } from '@nutians/shared/schemas';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../api.js';

function relativeTime(value: string, locale: string): string {
  const deltaSeconds = Math.round((new Date(value).getTime() - Date.now()) / 1000);
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 60 * 60 * 24 * 365],
    ['month', 60 * 60 * 24 * 30],
    ['day', 60 * 60 * 24],
    ['hour', 60 * 60],
    ['minute', 60],
  ];
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  for (const [unit, seconds] of units) {
    if (Math.abs(deltaSeconds) >= seconds) {
      return formatter.format(Math.round(deltaSeconds / seconds), unit);
    }
  }
  return formatter.format(deltaSeconds, 'second');
}

export default function ChatHistoryPage() {
  const { t, i18n } = useTranslation('coach');
  const [threads, setThreads] = useState<ChatThreadSummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadThreads = useCallback(
    async (nextCursor?: string | null) => {
      if (nextCursor) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const { data, response } = await api.GET('/me/chat/threads', {
          params: { query: { limit: 20, ...(nextCursor ? { cursor: nextCursor } : {}) } },
        });
        if (!response.ok || !data) throw new Error(`HTTP ${response.status}`);
        setThreads((prev) => (nextCursor ? [...prev, ...data.threads] : data.threads));
        setCursor(data.nextCursor);
      } catch {
        setError(t('history.loadError'));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [t],
  );

  useEffect(() => {
    void loadThreads();
  }, [loadThreads]);

  return (
    <div className="flex flex-1 min-h-0 flex-col">
      <div className="flex items-center justify-between gap-4 py-2">
        <div>
          <h2 className="text-xl font-medium tracking-tight">{t('history.title')}</h2>
          <p className="mt-1 text-sm text-ink-soft">{t('history.subtitle')}</p>
        </div>
        <Link
          to="/chat/new"
          className="inline-flex min-h-10 items-center rounded-lg bg-leaf px-4 text-sm font-medium text-white hover:bg-leaf-deep focus:outline-none focus:ring-2 focus:ring-leaf"
        >
          {t('history.newChat')}
        </Link>
      </div>

      <div className="mt-4 flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="py-12 text-center text-sm text-ink-soft">{t('history.loading')}</div>
        ) : error ? (
          <div className="py-12 text-center text-sm text-clay" role="alert">
            {error}
          </div>
        ) : threads.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-sm font-medium text-ink">{t('history.emptyTitle')}</p>
            <p className="mt-1 text-sm text-ink-soft">{t('history.emptyBody')}</p>
          </div>
        ) : (
          <div className="divide-y divide-husk border-y border-husk">
            {threads.map((thread) => (
              <Link
                key={thread.threadId}
                to={`/chat/${thread.threadId}`}
                className="block px-1 py-4 hover:bg-husk/40 focus:outline-none focus:ring-2 focus:ring-leaf"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-ink">{thread.title}</p>
                    <p className="mt-1 text-xs text-ink-soft">
                      {t('history.messageCount', { count: thread.messageCount })}
                    </p>
                  </div>
                  <time className="shrink-0 text-xs text-ink-soft" dateTime={thread.lastMessageAt}>
                    {relativeTime(thread.lastMessageAt, i18n.language)}
                  </time>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {cursor && !loading && !error ? (
        <button
          type="button"
          onClick={() => loadThreads(cursor)}
          disabled={loadingMore}
          className="mt-4 min-h-10 rounded-lg border border-husk px-4 text-sm font-medium text-ink hover:bg-husk/50 disabled:opacity-50"
        >
          {loadingMore ? t('history.loading') : t('history.loadMore')}
        </button>
      ) : null}
    </div>
  );
}
